import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { SvgIcon } from "./SvgIcon";
import useUserRole from "../hooks/useUserRole";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth } from "../firebase/config"; // Import auth for logout
import { signOut } from "firebase/auth"; // Import signOut function
import { BellIcon, GiftIcon, User } from "lucide-react";
import { FaEnvelope, FaEnvelopeOpen } from "react-icons/fa6";

export default function Header({ PageName, isSidebarOpen, setIsSidebarOpen }) {
  const { user, role } = useUserRole();
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const notificationRef = useRef(null);
  const settingsRef = useRef(null);

  const handleNotificationClick = async (notificationId) => {
    if (!user?.uid) return;
    console.log("Notification Id: ", notificationId);
    const notificationRef = doc(db, "notifications", notificationId);

    try {
      await updateDoc(notificationRef, {
        read: true,
      });
      console.log("Notification marked as read:", notificationId);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleNotificationOpen = () => {
    setIsNotificationOpen(!isNotificationOpen);
  };

  const handleSettingsClick = () => {
    if (role === "admin") {
      setIsSettingsModalOpen(true);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsSettingsModalOpen(false);
      console.log("User logged out successfully");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.uid) return;
    const batch = writeBatch(db);

    unreadNotifications.forEach((notification) => {
      const notificationRef = doc(db, "notifications", notification.id);
      batch.update(notificationRef, { read: true });
    });

    try {
      await batch.commit();
      console.log("All notifications marked as read");
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  useEffect(() => {
    // Handle clicks outside of notification dropdown
    function handleClickOutside(event) {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
      }
    }

    // Add event listener when dropdown is open
    if (isNotificationOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNotificationOpen]);

  // Handle clicks outside of settings modal
  useEffect(() => {
    function handleClickOutside(event) {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setIsSettingsModalOpen(false);
      }
    }

    if (isSettingsModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSettingsModalOpen]);

  useEffect(() => {
    if (user?.uid) {
      const notificationsRef = collection(db, "notifications");
      const q = query(notificationsRef, where("userId", "==", user.uid));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const notificationsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort notifications by timestamp in descending order
        const sortedNotifications = notificationsList.sort(
          (a, b) => b.timestamp - a.timestamp
        );

        setNotifications(sortedNotifications);
        setUnreadNotifications(
          sortedNotifications.filter((notif) => !notif.read)
        );
      });

      return () => unsubscribe();
    }
  }, [user]);

  return (
    <div className="w-[100%] flex justify-between items-center p-[18.5px] pt-0">
      {!isSidebarOpen && (
        <button
          className="text-[#A0AEC0] text-[12px] text-left flex md:me-28 sm:flex"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          <SvgIcon name={"hamburger"} />
        </button>
      )}

      <div className="">
        <div className="flex ">
          <h3 className="hidden md:inline-block text-[#A0AEC0] text-[12px] text-left">
            Pages/
          </h3>
          <h3 className="text-[16px] font-semibold text-white md:text-[12px]">
            {PageName}
          </h3>
        </div>
      </div>

      <div className="hidden lg:flex items-center gap-[15px]">
        <div className="relative flex " ref={notificationRef}>
          <button
            onClick={() => handleNotificationOpen()}
            className="relative p-2 hover:bg-gray-700 rounded-full transition-colors duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 13 13"
              fill="none"
            >
              <path
                d="M10.8146 8.49896C10.7757 8.45209 10.7375 8.40521 10.7 8.35998C10.1844 7.7363 9.87244 7.3599 9.87244 5.59435C9.87244 4.68029 9.65376 3.93029 9.22275 3.36779C8.90494 2.95224 8.47533 2.63701 7.90908 2.40404C7.90179 2.39999 7.89528 2.39467 7.88986 2.38834C7.68619 1.70631 7.12884 1.24951 6.50025 1.24951C5.87166 1.24951 5.31455 1.70631 5.11088 2.38764C5.10544 2.39374 5.09902 2.39888 5.09189 2.40287C3.77049 2.94685 3.1283 3.99053 3.1283 5.59365C3.1283 7.3599 2.81681 7.7363 2.30072 8.35927C2.26322 8.40451 2.22502 8.45044 2.18611 8.49826C2.08561 8.61946 2.02194 8.76691 2.00262 8.92317C1.98331 9.07943 2.00916 9.23795 2.07713 9.37998C2.22174 9.68466 2.52994 9.8738 2.88174 9.8738H10.1213C10.4715 9.8738 10.7776 9.6849 10.9227 9.38162C10.9909 9.23956 11.017 9.08091 10.9979 8.92447C10.9787 8.76803 10.9151 8.62036 10.8146 8.49896Z"
                fill="#718096"
              />
              <path
                d="M6.5002 11.7498C6.83888 11.7495 7.17117 11.6575 7.46183 11.4837C7.75249 11.3099 7.99068 11.0606 8.15114 10.7623C8.1587 10.748 8.16243 10.732 8.16198 10.7159C8.16152 10.6997 8.15689 10.6839 8.14854 10.6701C8.14019 10.6562 8.1284 10.6448 8.11432 10.6368C8.10024 10.6289 8.08434 10.6247 8.06817 10.6248H4.9327C4.91651 10.6247 4.90058 10.6288 4.88647 10.6368C4.87235 10.6447 4.86053 10.6561 4.85215 10.67C4.84377 10.6838 4.83913 10.6996 4.83866 10.7158C4.8382 10.732 4.84193 10.748 4.8495 10.7623C5.00994 11.0606 5.24809 11.3098 5.5387 11.4836C5.82932 11.6575 6.16156 11.7494 6.5002 11.7498Z"
                fill="#718096"
              />
            </svg>
            {unreadNotifications.length > 0 && (
              <span className="absolute top-1 right-0 w-[9px] h-[9px] bg-red-500 rounded-full"></span>
            )}
          </button>

          {isNotificationOpen && (
            <div className="absolute right-0 mt-2 w-[500px] bg-primary rounded shadow-lg overflow-hidden transform transition-all duration-200 ease-out origin-top-right animate-dropdown z-50">
              <div className="">
                <div className="flex gap-2 items-center h-30 p-2 m-2">
                  <BellIcon color="white" size={20} />
                  <h3 className="text-white text-start text-lg font-semibold ">
                    Notifications ({unreadNotifications?.length})
                    {unreadNotifications?.length > 0 && (
                      <sup className="text-red-500 text-xl">*</sup>
                    )}
                  </h3>
                  {
                    <button
                      className="ms-auto text-white bg-[#264FEE] p-2 cursor-pointer disabled:cursor-default rounded disabled:bg-[#2a314a]  disabled:text-gray-500 underline transition-colors duration-200"
                      onClick={handleMarkAllAsRead}
                      disabled={unreadNotifications.length === 0}
                    >
                      Marquer Tout Comme Lu
                    </button>
                  }
                </div>
                <div className="h-[1px] w-full bg-primary"> </div>
                <div className="space-y-[1px] max-h-[500px] overflow-y-auto scrollbar-thumb-gray-700 scrollbar-thin scrollbar-track-rounded-lg scrollbar-thumb-rounded-lg">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div className="notification-container  cursor-pointer">
                        <div
                          key={notification.id}
                          onClick={() =>
                            handleNotificationClick(notification.id)
                          }
                          className={`p-3 flex items-center gap-2 border-t border-t-1 border-t-gray-700  ${
                            notification.read
                              ? "bg-primary opacity-75"
                              : "bg-[#1f1f59]"
                          } transition-colors duration-200`}
                        >
                          {notification.type === "reward" ? (
                            <div className="w-12 h-12 me-2 rounded-full flex items-center justify-center bg-gray-600">
                              <GiftIcon size={20} color="white" />
                            </div>
                          ) : (
                            <div className="w-12 h-12 me-2 rounded-full flex items-center justify-center bg-gray-600">
                              {notification.thumbnailUrl ? (
                                <img
                                  src={notification.thumbnailUrl}
                                  alt="thumbnail"
                                  className="rounded-full w-12 h-12"
                                />
                              ) : (
                                <BellIcon size={20} color="white" />
                              )}
                            </div>
                          )}
                          <div className="flex flex-col">
                            <p className="text-white text-start text-sm">
                              {notification.tag}
                            </p>
                            <p className="text-white text-start text-sm truncate max-w-60">
                              Titre :{" "}
                              <span
                                className="font-extralight"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    notification.Titre || notification.title,
                                }}
                              ></span>
                            </p>
                            {notification.type === "video" && (
                              <>
                                <p className="text-white text-start text-sm truncate max-w-60">
                                  Section :{" "}
                                  <span
                                    className="font-extralight"
                                    dangerouslySetInnerHTML={{
                                      __html: notification.section,
                                    }}
                                  ></span>
                                </p>
                                <p className="text-white text-start text-sm truncate max-w-60">
                                  Module :{" "}
                                  <span
                                    className="font-extralight"
                                    dangerouslySetInnerHTML={{
                                      __html: notification.module,
                                    }}
                                  ></span>
                                </p>
                              </>
                            )}

                            <p className=" text-gray-400 text-start text-xs">
                              {new Date(
                                notification.timestamp?.seconds * 1000
                              ).toLocaleString()}
                            </p>
                          </div>
                          <div className="ms-auto">
                            {notification.read ? (
                              <FaEnvelopeOpen color="white" />
                            ) : (
                              <FaEnvelope color="white" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-center">
                      No notifications
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <span className="relative">
          {role === "admin" ? (
            <button
              onClick={handleSettingsClick}
              className="p-2 hover:bg-gray-700 rounded-full transition-colors duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 13 13"
                fill="none"
              >
                <path
                  d="M6.50015 4.62488C6.12931 4.62488 5.7668 4.73484 5.45846 4.94087C5.15012 5.1469 4.90979 5.43973 4.76788 5.78235C4.62597 6.12496 4.58883 6.50196 4.66118 6.86567C4.73353 7.22938 4.91211 7.56348 5.17433 7.8257C5.43655 8.08792 5.77064 8.2665 6.13436 8.33885C6.49807 8.4112 6.87507 8.37406 7.21768 8.23215C7.5603 8.09024 7.85313 7.84991 8.05916 7.54157C8.26519 7.23323 8.37515 6.87072 8.37515 6.49988C8.37367 6.00305 8.17565 5.527 7.82434 5.17569C7.47303 4.82438 6.99698 4.62636 6.50015 4.62488ZM10.5483 6.49988C10.5473 6.67508 10.5345 6.85001 10.5098 7.02347L11.651 7.91691C11.7007 7.95795 11.7342 8.01532 11.7455 8.07878C11.7568 8.14224 11.7451 8.20765 11.7127 8.26331L10.6331 10.1271C10.6003 10.1822 10.5491 10.2241 10.4885 10.2452C10.4278 10.2663 10.3617 10.2654 10.3017 10.2426L8.95992 9.70355C8.68051 9.9187 8.37476 10.0973 8.05007 10.2349L7.84945 11.6594C7.83822 11.7232 7.80518 11.7811 7.75598 11.8233C7.70677 11.8654 7.64446 11.8892 7.57968 11.8905H5.42062C5.35702 11.8893 5.29572 11.8665 5.24673 11.8259C5.19773 11.7854 5.16394 11.7294 5.15086 11.6671L4.95023 10.2426C4.62465 10.1066 4.31868 9.92774 4.04039 9.71081L2.69859 10.2499C2.63861 10.2727 2.5725 10.2737 2.51189 10.2526C2.45128 10.2315 2.40004 10.1897 2.36719 10.1346L1.28766 8.27105C1.25516 8.21538 1.24352 8.14998 1.25481 8.08652C1.2661 8.02306 1.29959 7.96568 1.3493 7.92464L2.49047 7.0312C2.46613 6.85512 2.45329 6.67763 2.45203 6.49988C2.453 6.32468 2.46585 6.14975 2.49047 5.97628L1.3493 5.08285C1.29959 5.04181 1.2661 4.98443 1.25481 4.92097C1.24352 4.85751 1.25516 4.7921 1.28766 4.73644L2.36719 2.87269C2.4 2.81752 2.45123 2.77568 2.51184 2.75455C2.57246 2.73341 2.63859 2.73433 2.69859 2.75714L4.04039 3.29621C4.31979 3.08105 4.62555 2.9025 4.95023 2.76488L5.15086 1.34035C5.16209 1.27654 5.19512 1.21861 5.24433 1.17646C5.29353 1.13431 5.35585 1.11055 5.42062 1.10925H7.57968C7.64328 1.11049 7.70458 1.13325 7.75358 1.17382C7.80257 1.21438 7.83637 1.27036 7.84945 1.33261L8.05007 2.75714C8.37606 2.89307 8.68244 3.07192 8.96109 3.28894L10.3017 2.74988C10.3617 2.72705 10.4278 2.72609 10.4884 2.74718C10.549 2.76827 10.6003 2.81006 10.6331 2.86519L11.7127 4.72894C11.7451 4.78461 11.7568 4.85001 11.7455 4.91347C11.7342 4.97693 11.7007 5.03431 11.651 5.07535L10.5098 5.96878C10.5342 6.14479 10.547 6.3222 10.5483 6.49988Z"
                  fill="#718096"
                />
              </svg>
            </button>
          ) : (
            <Link to={"/settings"}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 13 13"
                fill="none"
              >
                <path
                  d="M6.50015 4.62488C6.12931 4.62488 5.7668 4.73484 5.45846 4.94087C5.15012 5.1469 4.90979 5.43973 4.76788 5.78235C4.62597 6.12496 4.58883 6.50196 4.66118 6.86567C4.73353 7.22938 4.91211 7.56348 5.17433 7.8257C5.43655 8.08792 5.77064 8.2665 6.13436 8.33885C6.49807 8.4112 6.87507 8.37406 7.21768 8.23215C7.5603 8.09024 7.85313 7.84991 8.05916 7.54157C8.26519 7.23323 8.37515 6.87072 8.37515 6.49988C8.37367 6.00305 8.17565 5.527 7.82434 5.17569C7.47303 4.82438 6.99698 4.62636 6.50015 4.62488ZM10.5483 6.49988C10.5473 6.67508 10.5345 6.85001 10.5098 7.02347L11.651 7.91691C11.7007 7.95795 11.7342 8.01532 11.7455 8.07878C11.7568 8.14224 11.7451 8.20765 11.7127 8.26331L10.6331 10.1271C10.6003 10.1822 10.5491 10.2241 10.4885 10.2452C10.4278 10.2663 10.3617 10.2654 10.3017 10.2426L8.95992 9.70355C8.68051 9.9187 8.37476 10.0973 8.05007 10.2349L7.84945 11.6594C7.83822 11.7232 7.80518 11.7811 7.75598 11.8233C7.70677 11.8654 7.64446 11.8892 7.57968 11.8905H5.42062C5.35702 11.8893 5.29572 11.8665 5.24673 11.8259C5.19773 11.7854 5.16394 11.7294 5.15086 11.6671L4.95023 10.2426C4.62465 10.1066 4.31868 9.92774 4.04039 9.71081L2.69859 10.2499C2.63861 10.2727 2.5725 10.2737 2.51189 10.2526C2.45128 10.2315 2.40004 10.1897 2.36719 10.1346L1.28766 8.27105C1.25516 8.21538 1.24352 8.14998 1.25481 8.08652C1.2661 8.02306 1.29959 7.96568 1.3493 7.92464L2.49047 7.0312C2.46613 6.85512 2.45329 6.67763 2.45203 6.49988C2.453 6.32468 2.46585 6.14975 2.49047 5.97628L1.3493 5.08285C1.29959 5.04181 1.2661 4.98443 1.25481 4.92097C1.24352 4.85751 1.25516 4.7921 1.28766 4.73644L2.36719 2.87269C2.4 2.81752 2.45123 2.77568 2.51184 2.75455C2.57246 2.73341 2.63859 2.73433 2.69859 2.75714L4.04039 3.29621C4.31979 3.08105 4.62555 2.9025 4.95023 2.76488L5.15086 1.34035C5.16209 1.27654 5.19512 1.21861 5.24433 1.17646C5.29353 1.13431 5.35585 1.11055 5.42062 1.10925H7.57968C7.64328 1.11049 7.70458 1.13325 7.75358 1.17382C7.80257 1.21438 7.83637 1.27036 7.84945 1.33261L8.05007 2.75714C8.37606 2.89307 8.68244 3.07192 8.96109 3.28894L10.3017 2.74988C10.3617 2.72705 10.4278 2.72609 10.4884 2.74718C10.549 2.76827 10.6003 2.81006 10.6331 2.86519L11.7127 4.72894C11.7451 4.78461 11.7568 4.85001 11.7455 4.91347C11.7342 4.97693 11.7007 5.03431 11.651 5.07535L10.5098 5.96878C10.5342 6.14479 10.547 6.3222 10.5483 6.49988Z"
                  fill="#718096"
                />
              </svg>
            </Link>
          )}

          {/* Settings Modal - Small dropdown */}
          {isSettingsModalOpen && (
            <div
              ref={settingsRef}
              className="absolute right-0 mt-2 w-48 bg-gray-600 rounded shadow-lg overflow-hidden transform transition-all duration-200 ease-out origin-top-right z-50"
            >
              <div className="p-4">
                <h2 className="text-white text-start text-sm font-bold mb-3">
                  Settings
                </h2>
                <button
                  onClick={handleLogout}
                  className="w-full bg-red-400 hover:bg-red-700 text-white font-medium py-2 px-3 rounded transition-colors duration-200 text-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </span>
      </div>
      <div className="flex md:hidden">
        {user?.photoURL ? (
          <img
            src={user?.photoURL || ""}
            className="w-[35px] h-[35px] rounded-full"
            alt="Profile"
          />
        ) : (
          <div className=" rounded-full bg-[#7d7d7d] p-1">
            <User className="w-[25px] h-[25px] text-white" />
          </div>
        )}
      </div>
    </div>
  );
}
